import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren, Inject, PLATFORM_ID } from '@angular/core';
import { teamName } from 'src/app/core/utils/team-name';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { isPlatformBrowser, formatDate } from '@angular/common';
import * as _ from 'lodash';
import * as moment from 'moment';
import { combineLatest, firstValueFrom, map, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientLabService } from './../../../../core/services/patient-lab.service';
import { PatientService } from './../../../../core/services/patient.service';
import { UserService } from 'src/app/core/services/user.service';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import { AnalyticsService } from './../../../../core/services/analytics.service';
import { FileDownloadService } from './../../../../core/services/file-download.service';
import { LANCET_LOGO } from './../../../../core/data/app.data';
import { LabResultsDialogComponent, LabResultsDialogData } from '../../../../shared/components/lab-results-dialog/lab-results-dialog.component';
import { LabMonitoringDialogComponent } from '../../../../shared/components/lab-monitoring-dialog/lab-monitoring-dialog.component';
import { WhatsAppChatDialogComponent, WhatsAppChatDialogData } from '../../../../shared/components/whatsapp-chat-dialog/whatsapp-chat-dialog.component';
import { environment } from './../../../../../environments/environment';
import {
  matchesVendorDoctorRoles,
  VendorDoctorBadge,
  VendorDoctorBadgeCache,
  VendorDoctorBearing,
  vendorDoctorRoleOptions
} from './../../../../core/models/vendor-doctors.model';

@Component({
  selector: 'app-lab-orders-list',
  templateUrl: './lab-orders-list.component.html',
  styleUrls: ['./lab-orders-list.component.scss']
})
export class LabOrdersListComponent extends BaseComponent implements OnInit {
  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  public expandedHeader: string = 'completed';

  labOrders!: any[] | null;
  pendingLabOrders!: any[] | null;
  failedLabOrders!: any[] | null;
  completedLabOrders!: any[] | null;
  patient: any;
  selectedGroup: any;

  // Pre-computed data for display
  pendingLabOrdersDisplay: any[] = [];
  failedLabOrdersDisplay: any[] = [];
  completedLabOrdersDisplay: any[] = [];

  // Grouped data for display (like patient-invoicing-list)
  groupedPendingOrders: any[] = [];
  groupedFailedOrders: any[] = [];
  groupedCompletedOrders: any[] = [];

  userProfile: any;
  selectedDoctor: any = {};
  groupUsers: any[] = [];

  // Lab provider logos
  lancetLogo = LANCET_LOGO;

  // -----------------------------------------------------------------------
  // Vendor doctor badges and the role filter
  //
  // There is deliberately NO text filter on this surface — it never had one and this change
  // does not add one. The role filter is the only filter here.
  // -----------------------------------------------------------------------

  /**
   * OUR OWN vendor code, off the lab-orders envelope. `null` until a response has arrived, and
   * while it is null NOTHING is suppressed. Never a literal — see `vendor-doctors.model.ts`.
   */
  vendorDoctorPlatformCode: string | null = null;

  /**
   * Distinct role codes present on this patient's loaded orders, platform-code slots excluded.
   * Empty means the control is not rendered at all.
   */
  vendorDoctorRoleOptions: string[] = [];

  /** Ticked roles. Multi-select is OR; empty means no role filtering. */
  selectedVendorDoctorRoles: string[] = [];

  /** Per-row memo so `*ngFor` does not rebuild every chip on every change-detection pass. */
  private badgeCache = new VendorDoctorBadgeCache();

  /** A state unit (tasks/state-mode.md) orders nothing from a lab and sends nothing: existing orders are read-only. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private patientLabService: PatientLabService,
    private patientService: PatientService,
    private userService: UserService,
    private groupService: GroupService,
    private analyticsService: AnalyticsService,
    private fileDownloadService: FileDownloadService,
    override feedbackService: FeedbackService,
    override dialog: MatDialog,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    super(feedbackService, dialog);
  }

  ngOnInit(): void {
    this.loadUserProfile();
    this.loadPatient();
    this.loadGroupUsers();
    this.loadLabOrders();
  }

  private loadUserProfile(): void {
    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profile => {
        this.userProfile = profile;
      });
  }

  private loadPatient(): void {
    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(patient => {
        this.patient = patient;
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        this.selectedGroup = group;
        if (group?.id) {
          this.loadGroupUsers();
        }
      });
  }

  private loadGroupUsers(): void {
    if (!this.selectedGroup?.id) return;

    // Subscribe to groupUsers$ observable for updates
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(users => {
        if (users && users.length > 0) {
          this.groupUsers = users;
        }
      });

    // Fetch to ensure we have the latest data
    /* this.groupService.getGroupUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.users) {
            this.groupUsers = response.users;
          }
        },
        error: (error) => {
          console.error('Error loading group users:', error);
        }
      }); */
  }

  private loadLabOrders(): void {
    if (!this.patient?.id || !this.selectedGroup?.id) return;

    this.patientLabService.patientLabOrders$
      .pipe(takeUntil(this.destroy$))
      .subscribe((labOrders: any[] | null) => {
        this.labOrders = labOrders;
        this.categorizeLabOrders();
      });

    this.patientLabService.vendorDoctorPlatformCode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((code: string | null) => {
        this.vendorDoctorPlatformCode = code;
        // Every cached badge set was built against the OLD code, so every suppression
        // decision in it is now unverified.
        this.badgeCache.clear();
        this.categorizeLabOrders();
      });
  }

  private categorizeLabOrders(): void {
    if (!this.labOrders) {
      this.pendingLabOrders = [];
      this.failedLabOrders = [];
      this.completedLabOrders = [];
      this.pendingLabOrdersDisplay = [];
      this.failedLabOrdersDisplay = [];
      this.completedLabOrdersDisplay = [];
      this.vendorDoctorRoleOptions = [];
      return;
    }

    this.pendingLabOrders = this.labOrders.filter(order =>
      ['pending', 'ordered', 'in_progress'].includes(order.status)
    );

    this.failedLabOrders = this.labOrders.filter(order =>
      order.status === 'failed'
    );

    this.completedLabOrders = this.labOrders.filter(order =>
      ['completed', 'cancelled'].includes(order.status)
    );

    // The role filter's options come from EVERY loaded order for this patient, not from a
    // hard-coded vocabulary — the lab adds roles on its own schedule. Empty means no loaded
    // order names a role, and the control is not rendered at all.
    this.vendorDoctorRoleOptions = vendorDoctorRoleOptions(this.labOrders, this.vendorDoctorPlatformCode);

    // The role filter narrows what is DISPLAYED. `completedLabOrders` itself stays whole: the
    // continuous-monitoring dialog trends over it, and a trend computed from a filtered subset
    // would be a different clinical claim.
    // Pre-compute display data for pending orders
    this.pendingLabOrdersDisplay = this.applyRoleFilter(this.pendingLabOrders).map(order => ({
      ...order,
      statusColor: this.getStatusColor(order.status),
      priorityColor: this.getPriorityColor(order.priority),
      formattedDate: this.formatDate(order.orderDate),
      testNames: this.getTestNames(order.labTests),
      testCount: this.getTestCount(order.labTests),
      createdAt: order.createdAt || order.orderDate // Include created at field
    }));

    // Pre-compute display data for failed orders
    this.failedLabOrdersDisplay = this.applyRoleFilter(this.failedLabOrders).map(order => ({
      ...order,
      statusColor: this.getStatusColor(order.status),
      priorityColor: this.getPriorityColor(order.priority),
      formattedDate: this.formatDate(order.orderDate),
      testNames: this.getTestNames(order.labTests),
      testCount: this.getTestCount(order.labTests),
      createdAt: order.createdAt || order.orderDate // Include created at field
    }));

    // Pre-compute display data for completed orders
    this.completedLabOrdersDisplay = this.applyRoleFilter(this.completedLabOrders).map(order => {
      const abnormalCount = this.calculateAbnormalResults(order);
      const hasAbnormalResults = abnormalCount > 0;
      // Calculate display review status based on actual results, not database value
      const displayReviewStatus = hasAbnormalResults ? 'alert' : 'normal';

      return {
        ...order,
        statusColor: this.getStatusColor(order.status),
        priorityColor: this.getPriorityColor(order.priority),
        reviewStatusColor: this.getReviewStatusColor(displayReviewStatus),
        formattedDate: this.formatDate(order.orderDate),
        testNames: this.getTestNames(order.labTests),
        testCount: this.getTestCount(order.labTests),
        createdAt: order.createdAt || order.orderDate, // Include created at field
        abnormalResultsCount: abnormalCount,
        hasAbnormalResults: hasAbnormalResults,
        displayReviewStatus: displayReviewStatus // Calculated based on actual results
      };
    });

    // Group orders by date (like patient-invoicing-list)
    this.groupedPendingOrders = this.groupOrdersByDate(this.pendingLabOrdersDisplay);
    this.groupedFailedOrders = this.groupOrdersByDate(this.failedLabOrdersDisplay);
    this.groupedCompletedOrders = this.groupOrdersByDate(this.completedLabOrdersDisplay);
  }

  // -----------------------------------------------------------------------
  // Role filter — the interaction is the clinical-timeline one: a filter_list icon button
  // opening a mat-menu of mat-checkboxes, `(change)` re-applying, and stopPropagation on the
  // items so ticking a second role does not close the menu (and, here, so that clicking inside
  // the menu does not toggle the expansion panel behind it).
  // -----------------------------------------------------------------------

  /** Nothing ticked passes everything through, including the orders with no `vendorDoctors`. */
  private applyRoleFilter<T extends VendorDoctorBearing>(items: T[]): T[] {
    if (!this.selectedVendorDoctorRoles.length) {
      return items;
    }
    return items.filter(item =>
      matchesVendorDoctorRoles(item, this.selectedVendorDoctorRoles, this.vendorDoctorPlatformCode));
  }

  /** The chips for one order, memoised. Empty array => the template renders no container. */
  vendorDoctorBadgesFor(order: VendorDoctorBearing | null | undefined): ReadonlyArray<VendorDoctorBadge> {
    return this.badgeCache.badgesFor(order, this.vendorDoctorPlatformCode);
  }

  isVendorDoctorRoleSelected(role: string): boolean {
    return this.selectedVendorDoctorRoles.includes(role);
  }

  toggleVendorDoctorRole(role: string, checked: boolean): void {
    const without = this.selectedVendorDoctorRoles.filter(selected => selected !== role);
    this.selectedVendorDoctorRoles = checked ? [...without, role] : without;
    this.categorizeLabOrders();
  }

  clearVendorDoctorRoles(): void {
    if (!this.selectedVendorDoctorRoles.length) {
      return;
    }
    this.selectedVendorDoctorRoles = [];
    this.categorizeLabOrders();
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  toggleHeader(header: string): void {
    this.expandedHeader = this.expandedHeader === header ? '' : header;
  }

  private getStatusColor(status: string): string {
    switch (status) {
      case 'pending':
        return 'orange-chip';
      case 'ordered':
        return 'blue-chip';
      case 'in_progress':
        return 'green-chip';
      case 'completed':
        return 'green-chip';
      case 'failed':
        return 'red-chip';
      case 'cancelled':
        return 'red-chip';
      default:
        return 'grey-chip';
    }
  }

  private getPriorityColor(priority: string): string {
    switch (priority) {
      case 'routine':
        return 'grey-chip';
      case 'urgent':
        return 'orange-chip';
      case 'stat':
        return 'red-chip';
      default:
        return 'grey-chip';
    }
  }

  private getReviewStatusColor(reviewStatus: string): string {
    switch (reviewStatus) {
      case 'normal':
        return 'green-chip';
      case 'alert':
        return 'orange-chip';
      case 'critical':
        return 'red-chip';
      case 'pending':
        return 'grey-chip';
      default:
        return 'grey-chip';
    }
  }

  /**
   * Calculate the number of abnormal results from lab results data
   * This matches the logic used in lab-results-dialog component
   */
  private calculateAbnormalResults(order: any): number {
    if (!order.labResults) {
      return 0;
    }

    try {
      // Parse labResults if it's a string
      const labResults = typeof order.labResults === 'string'
        ? JSON.parse(order.labResults)
        : order.labResults;

      // Check if we have the summary with abnormalResults count (from aggregated results)
      if (labResults?.summary?.abnormalResults !== undefined) {
        return labResults.summary.abnormalResults;
      }

      // Otherwise, calculate from specimens
      if (!labResults?.specimens || !Array.isArray(labResults.specimens)) {
        return 0;
      }

      let abnormalCount = 0;

      labResults.specimens.forEach((specimen: any) => {
        if (specimen.procedures && Array.isArray(specimen.procedures)) {
          specimen.procedures.forEach((procedure: any) => {
            if (procedure.tests && Array.isArray(procedure.tests)) {
              procedure.tests.forEach((test: any) => {
                // Check for abnormal flag (same logic as lab-results-dialog)
                if (test.flag && test.flag.trim() !== '') {
                  abnormalCount++;
                }
              });
            }
          });
        }
      });

      return abnormalCount;
    } catch (error) {
      console.error('Error calculating abnormal results:', error);
      return 0;
    }
  }

  private formatDate(date: string): string {
    return moment(date).format('DD/MM/YYYY HH:mm');
  }


  editLabOrder(labOrder: any): void {
    this.analyticsService.trackButtonClick('Lab Order Edit');
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-lab-order', labOrder.id]
      }
    }]);
  }

  viewLabResults(labOrder: any): void {
    this.analyticsService.trackButtonClick('Lab Order View Results');

    const dialogData: LabResultsDialogData = {
      labOrder: labOrder,
      patient: this.patient,
      // `labOrder.vendorDoctors` is already on the row; only the code to suppress has to be
      // handed over, because it lives on the list ENVELOPE and not on the row.
      vendorDoctorPlatformCode: this.vendorDoctorPlatformCode
    };

    this.dialog.open(LabResultsDialogComponent, {
      data: dialogData,
      width: '90vw',
      /*  maxWidth: '900px', */
      height: '90vh',
      //maxHeight: '800px',
      disableClose: false,
      autoFocus: false
    });
  }

  async resubmitLabOrder(labOrder: any): Promise<void> {
    this.analyticsService.trackButtonClick('Lab Order Resubmit');

    const confirmed = await this.showConfirmationDialog(undefined, 'Are you sure you want to resubmit this lab order?', undefined, 'Cancel', 'Resubmit', false);

    if (confirmed) {
      this.patientLabService.resubmitLabOrder(labOrder.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.showToastMessage('Lab order resubmitted successfully');
              // Socket event will automatically update the list
            } else {
              this.showToastMessage('Failed to resubmit lab order');
            }
          },
          error: (error) => {
            console.error('Error resubmitting lab order:', error);
            this.showToastMessage('Error resubmitting lab order');
          }
        });
    }
  }

  async deleteLabOrder(labOrder: any): Promise<void> {
    this.analyticsService.trackButtonClick('Lab Order Delete');

    const confirmed = await this.showConfirmationDialog(undefined, 'Are you sure you want to delete this lab order?', undefined, 'Cancel', 'Delete', false);
    if (confirmed) {
      this.patientLabService.deleteLabOrder(labOrder.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.showToastMessage('Lab order deleted successfully');
              // Socket event will automatically remove the item from the list
            } else {
              this.showToastMessage('Failed to delete lab order');
            }
          },
          error: (error) => {
            console.error('Error deleting lab order:', error);
            this.showToastMessage('Error deleting lab order');
          }
        });
    }
  }

  private getTestNames(labTests: any[]): string {
    if (!labTests || labTests.length === 0) return 'No tests';
    return labTests.map(test => test.mnemonic || test.orderCode || test.name).join(', ');
  }

  private getTestCount(labTests: any[]): number {
    return labTests ? labTests.length : 0;
  }

  trackByOrderId(index: number, order: any): string {
    return order.id;
  }

  // Group orders by date (similar to patient-invoicing-list)
  private groupOrdersByDate(orders: any[]): any[] {
    if (!orders || orders.length === 0) return [];

    const groupedByDate: { [key: string]: any[] } = {};

    orders.forEach(order => {
      const dateKey = this.formatDateForGrouping(order.orderDate);
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }
      groupedByDate[dateKey].push(order);
    });

    // Convert to sorted array
    return Object.keys(groupedByDate)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map(date => ({
        date: new Date(date),
        orders: groupedByDate[date]
      }));
  }

  // Format date for grouping (YYYY-MM-DD)
  private formatDateForGrouping(dateStr: string): string {
    return new Date(dateStr).toISOString().split('T')[0];
  }

  // Get provider logo based on lab provider
  getProviderLogo(labProvider: string): string {
    if (!labProvider) return '';

    const provider = labProvider.toLowerCase();
    if (provider.includes('lancet')) {
      return this.lancetLogo;
    }

    return '';
  }

  openMonitoringDialog(event: Event): void {
    event.stopPropagation(); // Prevent expansion panel from opening

    this.analyticsService.trackButtonClick('Lab Monitoring Dialog Open');

    this.dialog.open(LabMonitoringDialogComponent, {
      data: {
        patient: this.patient,
        labOrders: this.completedLabOrders
      },
      width: '95vw',
      maxWidth: '1200px',
      height: '90vh',
      disableClose: false,
      autoFocus: false
    });
  }

  /**
   * The covering WhatsApp message for a pending lab order.
   *
   * This used to be the WHOLE artefact — order number, affiliate code, ordering doctor, ID
   * number, the full test list, all of it in a chat bubble the patient was told to show at the
   * counter. That is now the attached requisition PDF, which carries the same details on the
   * practice letterhead with the order number as a scannable barcode.
   *
   * What is left is what the PATIENT acts on: where to go, what to bring, whether to fast. It is
   * deliberately not a summary of the PDF — WhatsApp sends the document without a caption
   * (`whatsapp-chat-dialog.component.ts:2745`), so this text is what they read on their phone
   * while the form is what they hand over.
   */
  generatePendingLabOrderWhatsAppMessage(order: any): string {
    if (!this.patient || !this.selectedGroup || !order.labTests || order.labTests.length === 0) {
      return '';
    }

    const patientName = `${this.patient.name} ${this.patient.surname}`;
    const provider = this.getProviderInfo(order.labProvider);
    const providerName = provider.name || order.labProvider || 'the laboratory';

    let message = `Dear ${patientName} 👋

`;
    message += `${this.selectedGroup.name} has ordered laboratory tests for you.

`;
    message += `📄 *Your lab request form is attached.* Please show it on your phone or print it when you go — the laboratory needs the details on it.

`;

    if (order.notes && String(order.notes).trim()) {
      message += `📝 *A note from your doctor*
`;
      message += `${String(order.notes).trim()}

`;
    }

    message += `⚠️ *Before you go*
`;
    message += `• Visit any ${providerName} collection centre to have your samples taken
`;
    message += `• Some tests require fasting - please check with the lab if you are unsure
`;
    message += `• Bring a valid ID and your medical aid card (if applicable)
`;
    message += `• Your results are sent directly to your doctor

`;

    if (provider.labLocatorUrl) {
      message += `📍 Find your nearest ${providerName} collection centre: ${provider.labLocatorUrl}

`;
    }

    message += `If you have any questions about these tests, please contact our practice.

`;
    message += `Best regards,
`;
    message += `The ${teamName(this.selectedGroup.name)} Team`;

    return message;
  }

  /**
   * Fetch a fresh, short-lived URL for the order's requisition.
   *
   * The URL is deliberately NOT cached on the order: it carries a SAS token with a one-hour
   * expiry (`services/labOrderRequisitionService.js`), so a URL held from when the list was
   * loaded would 403 by the time anyone clicked it.
   *
   * @param order - The lab order.
   * @returns The URL and filename, or null if it could not be produced (already reported).
   */
  private async resolveRequisition(order: any): Promise<{ url: string; filename: string } | null> {
    try {
      const response: any = await firstValueFrom(this.patientLabService.getRequisitionUrl(order.id));
      if (!response?.success || !response?.url) {
        this.showToastMessage(response?.message || 'Could not produce the lab request form.');
        return null;
      }
      return { url: response.url, filename: response.filename };
    } catch (error) {
      console.error('Error resolving lab order requisition:', error);
      this.showToastMessage('Could not produce the lab request form.');
      return null;
    }
  }

  /**
   * Download the order's requisition.
   *
   * This used to build a PDF here with jsPDF, whose body was the WhatsApp message with the emoji
   * stripped out. It is now rendered on the server — one document, with the practice letterhead
   * and the order number as a scannable barcode, shared by the email, the WhatsApp message and
   * this button. See `tasks/lab-order-requisition-pdf.md`.
   */
  async downloadLabOrderRequisition(order: any): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const requisition = await this.resolveRequisition(order);
    if (!requisition) {
      return;
    }

    try {
      const response = await fetch(requisition.url);
      if (!response.ok) {
        throw new Error(`Requisition fetch failed: ${response.status}`);
      }
      const blob = await response.blob();
      await this.fileDownloadService.download(blob, requisition.filename);

      this.analyticsService.trackButtonClick('Lab Order Requisition Download');
    } catch (error) {
      console.error('Error downloading lab order requisition:', error);
      this.showToastMessage('Failed to download the lab request form.');
    }
  }

  /**
   * Open the WhatsApp composer with the covering message and the requisition attached.
   *
   * Opened programmatically rather than through `appWhatsappDialogTrigger`: the directive takes
   * its media as an `@Input`, and the requisition URL cannot be known until the click, because
   * producing it may mean rendering the PDF (which is what happens for a FAILED order, where
   * nothing has rendered one ahead of time).
   *
   * WhatsApp documents carry no caption, so the dialog sends the text first and then the
   * document (`whatsapp-chat-dialog.component.ts:2745`). That is why the message still has to
   * say everything the patient needs — the PDF is for the lab, the message is for the patient.
   */
  async sendLabOrderViaWhatsApp(order: any, isFailedOrder = false): Promise<void> {
    const message = isFailedOrder
      ? this.generateFailedLabOrderWhatsAppMessage(order)
      : this.generatePendingLabOrderWhatsAppMessage(order);

    const requisition = await this.resolveRequisition(order);

    this.analyticsService.trackButtonClick('Lab Order WhatsApp Share');

    const dialogData: WhatsAppChatDialogData = {
      patientId: this.patient?.id,
      patient: this.patient,
      initialMessage: message,
      // Shaped as the dialog's normaliser expects (`mediaUrl` + a MIME type on `mediaType`).
      // Left undefined when the render failed: the composer still opens with the message, which
      // in that case is the only thing the patient will get.
      initialMedia: requisition
        ? [{ mediaUrl: requisition.url, mediaType: 'application/pdf', mediaFileName: requisition.filename }]
        : undefined
    };

    this.dialog.open(WhatsAppChatDialogComponent, {
      width: '900px',
      maxWidth: '90vw',
      height: '80vh',
      maxHeight: '90vh',
      data: dialogData
    });
  }

  /**
   * The covering WhatsApp message for a FAILED lab order.
   *
   * A failed order never reached the lab electronically, so the form the patient carries is the
   * only record of it — which is why the attachment matters more here than anywhere else, and
   * why `resolveRequisition` renders one on demand for these (nothing rendered one ahead of
   * time; there was no success path to do it on).
   *
   * The identifying payload — ordering doctor, ID number, the test codes — is on that form.
   * Before it existed, this message carried an ID number and a list of codes and nothing naming
   * the clinician, and any result generated from it was an orphan by construction.
   */
  generateFailedLabOrderWhatsAppMessage(order: any): string {
    if (!this.patient || !this.selectedGroup || !order.labTests || order.labTests.length === 0) {
      return '';
    }

    const patientName = `${this.patient.name} ${this.patient.surname}`;
    const provider = this.getProviderInfo(order.labProvider);
    const providerName = provider.name || order.labProvider || 'the laboratory';

    let message = `Dear ${patientName} 👋

`;
    message += `${this.selectedGroup.name} has requested that you visit ${providerName} to have laboratory tests performed.

`;
    message += `📄 *Your lab request form is attached.* Please show it on your phone or print it and hand it in at the collection centre — it lists the tests required and the doctor who ordered them.

`;

    if (order.notes && String(order.notes).trim()) {
      message += `📝 *A note from your doctor*
`;
      message += `${String(order.notes).trim()}

`;
    }

    message += `⚠️ *Before you go*
`;
    message += `• Visit any ${providerName} collection centre
`;
    message += `• Some tests require fasting - please check with the lab if you are unsure
`;
    message += `• Bring a valid ID and your medical aid card (if applicable)
`;
    message += `• Your results are sent directly to your doctor

`;

    if (provider.labLocatorUrl) {
      message += `📍 Find your nearest ${providerName} collection centre: ${provider.labLocatorUrl}

`;
    }

    message += `If you have any questions, please contact our practice.

`;
    message += `Best regards,
`;
    message += `The ${teamName(this.selectedGroup.name)} Team`;

    return message;
  }

  /**
   * `orderingDoctorLabel` and `getDoctorFromGroupUsers` USED TO LIVE HERE.
   *
   * They rendered `Ordering Doctor: DR X Y (MNEMONIC)` into the two WhatsApp messages and the
   * two jsPDF documents this component built — the client's mirror of `orderingDoctorLabel` in
   * `utils/labOrderPresentation.js`, kept in step with it by hand.
   *
   * All four of those artefacts are gone. The clinician's identity is now rendered ONCE, on the
   * server, into the requisition PDF that the email and the WhatsApp message both attach
   * (`services/labOrderPdfService.js`). The mirror was deleted rather than left in place because
   * a second implementation of that rule, no longer rendering anything, is exactly the thing
   * that drifts and then silently disagrees — which is the defect this whole area was repaired
   * for. `groupUsers` is still loaded; it feeds the vendor-doctor badges.
   */

  /**
   * Get provider information based on lab provider name
   */
  private getProviderInfo(labProvider: string): any {
    if (!labProvider) {
      return { name: 'Laboratory', processingTime: '24-48 hours' };
    }

    const provider = labProvider.toLowerCase();

    if (provider.includes('lancet')) {
      return {
        name: 'Lancet Laboratories',
        processingTime: '24-48 hours',
        labLocatorUrl: 'https://www.lancet.co.za/appointment/'
      };
    }

    // Default provider info
    return {
      name: labProvider,
      processingTime: '24-48 hours'
    };
  }
}
